import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Users, Loader2, ChevronDown, ChevronUp, Building2, Briefcase, Navigation,
} from 'lucide-react';

import { SectionHeader } from '../../components/vendor/sharedVendorUI';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import AddSupplierTradingVendor from './AddSupplierTradingVendor';
import AddServiceProviderVendor from './AddServiceProviderVendor';

import {
    fetchAllVendors, fetchVendorDetailsByCode,
    selectVendorsArray, selectVendorsLoading,
    selectVendorDetailsByCode, selectVendorDetailsLoading,
} from '../../slices/purchaseSlice/vendorCreationSlice';

const DetailRow = ({ label, value }) => (
    <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || '—'}</p>
    </div>
);

const VendorDetailPanel = ({ vendor, loading }) => {
    if (loading) {
        return (
            <div className="flex items-center justify-center py-6 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
        );
    }
    if (!vendor) return null;

    const isSP = vendor.Type === 'Service Provider';

    return (
        <div className="p-5 bg-gray-50/60 dark:bg-gray-900/30 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <DetailRow label="Type" value={vendor.Type} />
                <DetailRow label="Vendor Name" value={vendor.VendorName} />
                <DetailRow label="Address" value={vendor.Address} />
                <DetailRow label="Phone No" value={vendor.Phoneno} />
                <DetailRow label="Mobile No" value={vendor.MobileNo} />
                <DetailRow label="Bank Name" value={vendor.BankName} />
                <DetailRow label="Account No" value={vendor.AccountNo} />
                <DetailRow label="IFSC Code" value={vendor.IFSCode} />
                <DetailRow label="Nature Group" value={vendor.NatureGroupName} />
                <DetailRow label="Master Group" value={vendor.MasterGroup} />
                {vendor.SubGroupId !== 0 && <DetailRow label="Sub Group" value={vendor.SubGroup} />}
                <DetailRow label="Email" value={vendor.EmailId} />
                <DetailRow label="PAN No" value={vendor.PanNo} />
                {!isSP && <DetailRow label="CST No" value={vendor.CstNo} />}
                <DetailRow label="GST Applicable" value={/^(1|yes)$/i.test(String(vendor.GstApplicable ?? '').trim()) ? 'Yes' : 'No'} />

                {isSP && (
                    <>
                        <DetailRow label="PF Reg No" value={vendor.PFRegNo} />
                        <DetailRow label="SP Category" value={vendor.SPCategory} />
                        <DetailRow label="Owner ID / KYC" value={vendor.OwnerIDKYC} />
                        <DetailRow label="Vehicle/Equipment RC & Insurance" value={vendor.VehicleEquipmentRCInsurance} />
                        <DetailRow label="ESI Registration" value={vendor.ESIRegistration} />
                        <DetailRow label="Labour License" value={vendor.LabourLicense} />
                        <DetailRow label="Electrical Contractor License" value={vendor.ElectricalContractorLicense} />
                        <DetailRow label="Insurance (WC/Liability)" value={vendor.InsuranceWCLiability} />
                        <DetailRow label="Past Experience Proof" value={vendor.PastExperienceProof} />
                        <DetailRow label="Manpower List & Certifications" value={vendor.ManpowerListCertifications} />
                        <DetailRow label="Tools & Equipment List" value={vendor.ToolsEquipmentList} />
                        <DetailRow label="QA/QC Procedures" value={vendor.QAQCProcedures} />
                        <DetailRow label="HSE Policy/Safety Plan" value={vendor.HSEPolicySafetyPlan} />
                        <DetailRow label="Financial Statements/ITR" value={vendor.FinancialStatementsITR} />
                        <DetailRow label="Bank Statement/WC Proof" value={vendor.BankStatementWCProof} />
                        <DetailRow label="Performance BG/Security" value={vendor.PerformanceBGSecurity} />
                        <DetailRow label="Client-Aligned B2B Docs" value={vendor.ClientAlignedB2BDocs} />
                        <DetailRow label="Approval Validity" value={vendor.ApprovalValidity} />
                    </>
                )}
            </div>

            {vendor.MSMEApplicable === 'Yes' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <DetailRow label="MSME Applicable" value={vendor.MSMEApplicable} />
                    <DetailRow label="Udyam Registration Number" value={vendor.UdyamRegistrationNumber} />
                    <DetailRow label="Enterprise Type" value={vendor.Enterprisetype} />
                    <DetailRow label="Major Activity" value={vendor.MajorActivity} />
                </div>
            )}

            {vendor.Ledger && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <DetailRow label="Opening Balance" value={`₹ ${formatIndianCurrency(vendor.Ledger.Balance)}`} />
                    <DetailRow label="Balance As On Date" value={vendor.Ledger.Date} />
                    <DetailRow label="Ledger Value Type" value={vendor.Ledger.ValueType} />
                </div>
            )}

            {vendor.VendorGSTData?.length > 0 && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">GST Details</p>
                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-800">
                                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">State</th>
                                    <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">GST No</th>
                                </tr>
                            </thead>
                            <tbody>
                                {vendor.VendorGSTData.map((g, i) => (
                                    <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{g.StateName}</td>
                                        <td className="px-4 py-2 text-gray-700 dark:text-gray-300">{g.TaxNo}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

const VendorRow = ({ vendor, roleId, dispatch, isExpanded, onToggle }) => {
    const details = useSelector(selectVendorDetailsByCode(vendor.VendorCode));
    const detailsLoading = useSelector(selectVendorDetailsLoading);

    const handleToggle = () => {
        onToggle(vendor.VendorCode);
        if (!isExpanded && !details) {
            dispatch(fetchVendorDetailsByCode({ vendorCode: vendor.VendorCode, roleId }));
        }
    };

    return (
        <>
            <tr
                onClick={handleToggle}
                className="border-t border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30"
            >
                <td className="px-4 py-3 text-center">
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400 mx-auto" /> : <ChevronDown className="h-4 w-4 text-gray-400 mx-auto" />}
                </td>
                <td className="px-4 py-3 text-gray-800 dark:text-gray-100">{vendor.Type}</td>
                <td className="px-4 py-3 text-gray-800 dark:text-gray-100 font-medium">{vendor.VendorName}</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{vendor.Address}</td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={4} className="p-0">
                        <VendorDetailPanel vendor={details} loading={detailsLoading && !details} />
                    </td>
                </tr>
            )}
        </>
    );
};

const VendorCreation = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID || 0;

    const vendors = useSelector(selectVendorsArray);
    const vendorsLoading = useSelector(selectVendorsLoading);

    const [activeForm, setActiveForm] = useState(null); // null | 'supplier' | 'sp'
    const [expandedCode, setExpandedCode] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        dispatch(fetchAllVendors());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const handleSuccess = () => {
        setActiveForm(null);
        dispatch(fetchAllVendors());
    };

    const filteredVendors = vendors.filter((v) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return v.VendorName?.toLowerCase().includes(q) || v.Address?.toLowerCase().includes(q) || v.Type?.toLowerCase().includes(q);
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Users className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Vendor Details'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Register and review Supplier/Trading and Service Provider vendors</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / Vendor Creation</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {activeForm === 'supplier' && (
                    <AddSupplierTradingVendor onSuccess={handleSuccess} onCancel={() => setActiveForm(null)} />
                )}
                {activeForm === 'sp' && (
                    <AddServiceProviderVendor onSuccess={handleSuccess} onCancel={() => setActiveForm(null)} />
                )}

                {!activeForm && (
                    <>
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Users} title="Vendors" subtitle="Register a new vendor" />
                            <div className="p-6 md:p-8 flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => setActiveForm('supplier')}
                                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow hover:from-indigo-600 hover:to-purple-700 transition-all"
                                >
                                    <Building2 className="h-4 w-4" /> Add Supplier/Trading Vendor
                                </button>
                                <button
                                    onClick={() => setActiveForm('sp')}
                                    className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow hover:from-teal-600 hover:to-emerald-700 transition-all"
                                >
                                    <Briefcase className="h-4 w-4" /> Add ServiceProvider Vendor
                                </button>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Users} title="Vendor List" subtitle="Click a row to view full details" />
                            <div className="p-6 md:p-8">
                                <input
                                    type="text"
                                    placeholder="Search by name, address or type…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full max-w-sm mb-4 px-3.5 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100"
                                />
                                {vendorsLoading ? (
                                    <div className="flex items-center justify-center py-10 text-gray-400">
                                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                                    </div>
                                ) : filteredVendors.length === 0 ? (
                                    <div className="px-4 py-6 rounded-xl text-sm font-medium bg-gray-50 dark:bg-gray-900/30 border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-center">
                                        No Vendors Found
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                                                    <th className="px-4 py-3 w-10"></th>
                                                    <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Type</th>
                                                    <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Vendor Name</th>
                                                    <th className="px-4 py-3 text-left text-[10px] font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">Address</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredVendors.map((v) => (
                                                    <VendorRow
                                                        key={v.VendorCode}
                                                        vendor={v}
                                                        roleId={roleId}
                                                        dispatch={dispatch}
                                                        isExpanded={expandedCode === v.VendorCode}
                                                        onToggle={(code) => setExpandedCode((prev) => (prev === code ? null : code))}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default VendorCreation;
